import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { LogIn, LogOut, Crown, Loader2, Ban } from 'lucide-react';
import type { GuildMemberRole } from '@/lib/api';

interface GuildJoinButtonProps {
  myRole: GuildMemberRole | null | undefined;
  visibility: string;
  onJoin: () => Promise<void>;
  onLeave: () => Promise<void>;
}

export function GuildJoinButton({ myRole, visibility, onJoin, onLeave }: GuildJoinButtonProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [banned, setBanned] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);

  const handleAction = async (action: () => Promise<void>) => {
    setLoading(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Something went wrong';
      if (message.toLowerCase().includes('banned')) {
        setBanned(true);
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Button disabled>
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        Loading...
      </Button>
    );
  }

  if (banned) {
    return (
      <div className="flex flex-col items-end gap-1">
        <Button variant="destructive" disabled aria-label="You are banned from this guild">
          <Ban className="h-4 w-4 mr-2" />
          Banned
        </Button>
        <p className="text-xs text-destructive">You are banned from this guild</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-end gap-1">
        <Button onClick={() => handleAction(onJoin)} aria-label="Join guild">
          <LogIn className="h-4 w-4 mr-2" />
          Join Guild
        </Button>
        <p className="text-xs text-destructive">{error}</p>
      </div>
    );
  }

  if (myRole === 'owner') {
    return (
      <Button variant="outline" disabled aria-label="You are the owner">
        <Crown className="h-4 w-4 mr-2" />
        Owner
      </Button>
    );
  }

  if (myRole) {
    return (
      <>
        <Dialog open={showLeaveConfirm} onOpenChange={setShowLeaveConfirm}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Leave Guild</DialogTitle>
              <DialogDescription>
                Are you sure you want to leave this guild? You will lose your current role and may
                need to rejoin.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowLeaveConfirm(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  setShowLeaveConfirm(false);
                  handleAction(onLeave);
                }}
              >
                Leave
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Button
          variant="outline"
          onClick={() => setShowLeaveConfirm(true)}
          aria-label="Leave guild"
        >
          <LogOut className="h-4 w-4 mr-2" />
          Leave Guild
        </Button>
      </>
    );
  }

  if (visibility === 'private') {
    return (
      <Button variant="outline" disabled aria-label="This guild is invite only">
        <LogIn className="h-4 w-4 mr-2" />
        Invite Only
      </Button>
    );
  }

  return (
    <Button onClick={() => handleAction(onJoin)} aria-label="Join guild">
      <LogIn className="h-4 w-4 mr-2" />
      Join Guild
    </Button>
  );
}
